import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import { navurl } from "@/Config/Routeconfig/NavigationUrl";
import InputTextField from "@/Components/Form/InputTextField";
import DropdownField from "@/Components/Form/InputDropdown";
import TypographyText from "@/Components/Form/TypographyText";
import { BlackButton } from "@/Components/Buttons/FiledButton";

import { useLoginMutation, useGetOrganizationQuery } from "@/redux/Login/Login";
import { useForgotPasswordMutation } from "@/redux/ForgotPassword/ForgotPasseord";

function LoginPage() {
  const navigate = useNavigate();
  const [forgotLoading, setForgotLoading] = useState(false);
  const [login, { isLoading: loginApiLoading }] = useLoginMutation();
  const [forgotPassword, { isLoading: forgotApiLoading }] =
    useForgotPasswordMutation();
  const { data: organizations = [], isLoading: orgLoading } =
    useGetOrganizationQuery();

  const [formValues, setFormValues] = useState({
    organization: "",
    username: "",
    password: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" })); // Clear error on change
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const [apiError, setApiError] = useState(""); // 🔥 new state

  const handleLoginSubmit = async () => {
    setSuccessMessage("");
    setApiError(""); // clear old api error
    let errors = {};

    if (!formValues.organization)
      errors.organization = "Organization is required.";

    const username = formValues.username?.trim();
    if (!username) {
      errors.username = "Username is required.";
    } else if (username.length < 4) {
      errors.username = "Username must be at least 4 characters.";
    }

    if (!formValues.password?.trim()) errors.password = "Password is required.";

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const response = await login({
        userName: username,
        userPassword: formValues.password.trim(),
        organizationId: formValues.organization,
      }).unwrap();

      if (response) {
        localStorage.setItem("userId", response.userId);
        localStorage.setItem("userName", response.userName);
        localStorage.setItem("userToken", response.userBearerToken);
        setSuccessMessage("Login successful!...");
        setTimeout(() => navigate(navurl.Dashboard), 1000);
      }
    } catch (err) {
      const message = err?.data?.message || "Login failed. Please try again.";

      let fieldMap = {};
      if (/organization/i.test(message)) {
        fieldMap.organization = message;
      } else if (/user/i.test(message)) {
        fieldMap.username = message;
      } else if (/pass/i.test(message)) {
        fieldMap.password = message;
      } else {
        setApiError(message);
      }

      if (Object.keys(fieldMap).length > 0) {
        setFieldErrors(fieldMap);
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!formValues.username?.trim()) {
      setFieldErrors({ username: "Username is required." });
      return;
    }

    setFieldErrors({});
    setSuccessMessage("");
    setForgotLoading(true); // 🔥 Start loading

    try {
      const [response] = await Promise.all([
        forgotPassword(formValues.username.trim()).unwrap(),
        delay(2000),
      ]);

      if (response) {
        localStorage.setItem("userId", response?.userId);
        localStorage.setItem("phone", response?.phone);
        localStorage.setItem("userName", response?.userName);
        setSuccessMessage("OTP sent successfully. Redirecting...");
        navigate(navurl.ForgotPassword);
      } else {
        setFieldErrors({ username: "Could not send OTP. Please try again." });
      }
    } catch (err) {
      setFieldErrors({
        username: err?.data?.message || "Could not process password reset.",
      });
    } finally {
      setForgotLoading(false); // 🔥 Stop loading
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ py: 2, display: "flex", flexDirection: "column" }}>
        <Typography variant="heading">Welcome Back!</Typography>
        <Typography variant="bodySmall" color="text.secondary">
          Version 6.8H
        </Typography>
      </Box>

      <DropdownField
        label="Organization"
        placeholder={orgLoading ? "Loading..." : "Select Organization"}
        value={formValues.organization}
        onChange={(val) => handleChange("organization", val)}
        options={organizations.map((org) => ({
          label: org.name,
          value: org.id,
        }))}
        required
        sx={{ mb: 1 }}
        disabled={orgLoading}
        error={!!fieldErrors.organization}
        helperText={fieldErrors.organization || ""}
      />

      <InputTextField
        label="Username"
        placeholder="Enter Username"
        value={formValues.username}
        onChange={(e) => handleChange("username", e.target.value)}
        required
        error={!!fieldErrors.username} // ✅ ensures red error style
        helperText={fieldErrors.username || ""} // ✅ shows message
      />

      <InputTextField
        label="Password"
        type="password"
        placeholder="Enter Password"
        value={formValues.password}
        onChange={(e) => handleChange("password", e.target.value)}
        required
        showToggle
        sx={{ mt: 1 }}
        error={!!fieldErrors.password}
        helperText={fieldErrors.password || ""}
      />

      <TypographyText
        label="Forgot Password?"
        onClick={handleForgotPassword}
        loading={forgotLoading} 
        sx={{ mt: 2 }}
      />

      <BlackButton
        label={loginApiLoading ? "Signing In..." : "Sign In"}
        onClick={handleLoginSubmit}
        disabled={loginApiLoading || orgLoading}
        fullWidth
        width="100%"
        sx={{ mt: 2 }}
      />
      {apiError && (
        <Typography color="error.main" sx={{ mt: 1, textAlign: "center" }}>
          {apiError}
        </Typography>
      )}

      {successMessage && (
        <Typography color="success.main" sx={{ mt: 1, textAlign: "center" }}>
          {successMessage}
        </Typography>
      )}
    </Box>
  );
}

export default LoginPage;
